import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useGamification } from '../../contexts/GamificationContext';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiMail, FiPhone, FiMapPin, FiBook, FiAward, FiUpload, FiLink, FiSave, FiCreditCard, FiCheck, FiFileText } = FiIcons;

const CandidateProfile = () => {
  const { user, updateProfile } = useAuth();
  const { addPoints, awardBadge } = useGamification();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: user.name || '',
    email: user.email || '',
    phone: user.phone || '',
    city: user.city || '',
    placeOfResidence: user.placeOfResidence || '',
    gender: user.gender || '',
    maritalStatus: user.maritalStatus || '',
    cardOwnership: user.cardOwnership || '',
    field: user.field || '',
    experience: user.experience || '',
    skills: user.skills || '',
    education: user.education || '',
    portfolio: user.portfolio || '',
    cvName: user.cvName || ''
  });
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Check if this is first time completing profile
    const wasIncomplete = !user.profileComplete;
    
    updateProfile(formData);
    setSuccess(true);
    setLoading(false);
    
    // Award points and badges for profile completion
    if (wasIncomplete) {
      addPoints('profileCompletion');
      awardBadge('firstProfile');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploading(true);
      
      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const updatedFormData = { ...formData, cvName: file.name };
      setFormData(updatedFormData);
      
      // Update user profile immediately with new CV
      updateProfile(updatedFormData);
      
      addPoints('cvUpload');
      setUploading(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    }
  };

  const generateQRCode = () => {
    const profileUrl = `${window.location.origin}/candidate/${user.id}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(profileUrl)}`;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Candidate Profile</h1>
        <p className="text-gray-600">
          Complete your profile to help companies find and connect with you. 
          <span className="text-blue-600 font-medium"> Earn points for each section!</span>
        </p>
      </motion.div>

      {success && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md mb-6 flex items-center space-x-2">
          <SafeIcon icon={FiCheck} />
          <span>Changes saved successfully! Points earned!</span>
        </motion.div>
      )}

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Profile Form */}
        <div className="lg:col-span-2">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information Section */}
              <div className="border-b border-gray-200 pb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
                  <span className="text-sm text-blue-600 font-medium">+50 pts</span>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SafeIcon icon={FiUser} className="text-gray-400" />
                      </div>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Your full name"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SafeIcon icon={FiMail} className="text-gray-400" />
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SafeIcon icon={FiPhone} className="text-gray-400" />
                      </div>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                      Gender *
                    </label>
                    <select
                      id="gender"
                      name="gender"
                      required
                      value={formData.gender}
                      onChange={handleChange}
                      className="block w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select gender...</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                      <option value="prefer-not-to-say">Prefer not to say</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="maritalStatus" className="block text-sm font-medium text-gray-700 mb-2">
                      Marital Status *
                    </label>
                    <select
                      id="maritalStatus"
                      name="maritalStatus"
                      required
                      value={formData.maritalStatus}
                      onChange={handleChange}
                      className="block w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select status...</option>
                      <option value="single">Single</option>
                      <option value="married">Married</option>
                      <option value="divorced">Divorced</option>
                      <option value="widowed">Widowed</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="cardOwnership" className="block text-sm font-medium text-gray-700 mb-2">
                      Card Ownership *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SafeIcon icon={FiCreditCard} className="text-gray-400" />
                      </div>
                      <select
                        id="cardOwnership"
                        name="cardOwnership"
                        required
                        value={formData.cardOwnership}
                        onChange={handleChange}
                        className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select option...</option>
                        <option value="yes">Yes, I own a credit/debit card</option>
                        <option value="no">No, I don't own a card</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Information Section */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Location Information</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="placeOfResidence" className="block text-sm font-medium text-gray-700 mb-2">
                      Place of Residence *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SafeIcon icon={FiMapPin} className="text-gray-400" />
                      </div>
                      <input
                        id="placeOfResidence"
                        name="placeOfResidence"
                        type="text"
                        required
                        value={formData.placeOfResidence}
                        onChange={handleChange}
                        className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Full residential address"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                      City/Country *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SafeIcon icon={FiMapPin} className="text-gray-400" />
                      </div>
                      <input
                        id="city"
                        name="city"
                        type="text"
                        required
                        value={formData.city}
                        onChange={handleChange}
                        className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="New York, USA"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Professional Information Section */}
              <div className="border-b border-gray-200 pb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Professional Information</h3>
                  <span className="text-sm text-blue-600 font-medium">+20 pts each</span>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="field" className="block text-sm font-medium text-gray-700 mb-2">
                      Field/Specialization *
                    </label>
                    <select
                      id="field"
                      name="field"
                      required
                      value={formData.field}
                      onChange={handleChange}
                      className="block w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select field...</option>
                      <option value="Software Engineering">Software Engineering</option>
                      <option value="Data Science">Data Science</option>
                      <option value="Product Management">Product Management</option>
                      <option value="Design">Design</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Sales">Sales</option>
                      <option value="Finance">Finance</option>
                      <option value="Operations">Operations</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
                      Years of Experience *
                    </label>
                    <select
                      id="experience"
                      name="experience"
                      required
                      value={formData.experience}
                      onChange={handleChange}
                      className="block w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select experience...</option>
                      <option value="0-1">0-1 years (Entry Level)</option>
                      <option value="1-3">1-3 years (Junior)</option>
                      <option value="3-5">3-5 years (Mid Level)</option>
                      <option value="5-8">5-8 years (Senior)</option>
                      <option value="8+">8+ years (Lead/Principal)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="skills" className="block text-sm font-medium text-gray-700 mb-2">
                    Skills *
                  </label>
                  <textarea
                    id="skills"
                    name="skills"
                    rows={3}
                    required
                    value={formData.skills}
                    onChange={handleChange}
                    className="block w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="React, Node.js, Python, Machine Learning, etc."
                  />
                </div>
                <div>
                  <label htmlFor="education" className="block text-sm font-medium text-gray-700 mb-2">
                    Education Summary *
                  </label>
                  <div className="relative">
                    <div className="absolute top-3 left-3 pointer-events-none">
                      <SafeIcon icon={FiBook} className="text-gray-400" />
                    </div>
                    <textarea
                      id="education"
                      name="education"
                      rows={3}
                      required
                      value={formData.education}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Bachelor's in Computer Science from XYZ University, 2020"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Information Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
                <div>
                  <label htmlFor="portfolio" className="block text-sm font-medium text-gray-700 mb-2">
                    Portfolio/Website Link
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiLink} className="text-gray-400" />
                    </div>
                    <input
                      id="portfolio"
                      name="portfolio"
                      type="url"
                      value={formData.portfolio}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://yourportfolio.com"
                    />
                  </div>
                </div>
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CV Upload
                  </label>
                  <div 
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                      formData.cvName 
                        ? 'border-green-400 bg-green-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`} 
                    onClick={handleUploadClick}
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                        <p className="text-gray-600">Uploading...</p>
                      </>
                    ) : formData.cvName ? (
                      <>
                        <SafeIcon icon={FiFileText} className="text-green-600 text-3xl mx-auto mb-2" />
                        <p className="text-green-700 font-medium mb-1">{formData.cvName}</p>
                        <p className="text-sm text-green-600">Click to replace</p>
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiUpload} className="text-gray-400 text-3xl mx-auto mb-2" />
                        <p className="text-gray-600 mb-2">Click to upload or drag and drop</p>
                        <p className="text-sm text-gray-500">PDF, DOC, DOCX up to 5MB</p>
                        <p className="text-sm text-blue-600 font-medium mt-2">+30 points!</p>
                      </>
                    )}
                    <input 
                      type="file" 
                      ref={fileInputRef}
                      className="hidden" 
                      accept=".pdf,.doc,.docx" 
                      onChange={handleFileChange}
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <SafeIcon icon={FiSave} />
                    <span>Save Profile & Earn Points</span>
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>

        {/* QR Code & Profile Summary */}
        <div className="space-y-6">
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Your QR Code</h3>
            <div className="text-center">
              <img src={generateQRCode()} alt="Profile QR Code" className="mx-auto mb-4 border border-gray-200 rounded-lg" />
              <p className="text-sm text-gray-600">
                Companies can scan this code to view your profile and schedule interviews.
              </p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Completion</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span>Personal Info</span>
                <span className={formData.name && formData.email && formData.phone && formData.gender && formData.maritalStatus && formData.cardOwnership ? "text-green-600 font-medium" : "text-yellow-600 font-medium"}>
                  {formData.name && formData.email && formData.phone && formData.gender && formData.maritalStatus && formData.cardOwnership ? '✓ Complete' : '⚠ Incomplete'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Location Info</span>
                <span className={formData.placeOfResidence && formData.city ? "text-green-600 font-medium" : "text-yellow-600 font-medium"}>
                  {formData.placeOfResidence && formData.city ? '✓ Complete' : '⚠ Incomplete'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Professional Info</span>
                <span className={formData.field && formData.experience && formData.skills && formData.education ? "text-green-600 font-medium" : "text-yellow-600 font-medium"}>
                  {formData.field && formData.experience && formData.skills && formData.education ? '✓ Complete' : '⚠ Incomplete'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>CV Upload</span>
                <span className={formData.cvName ? "text-green-600 font-medium" : "text-yellow-600 font-medium"}>
                  {formData.cvName ? '✓ Complete' : '⚠ Pending'}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CandidateProfile;